import React, { useEffect, useState } from "react";

const sanitizeQuizHtml = (html) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(html, "text/html");

  document
    .querySelectorAll("script, style, link, meta, title")
    .forEach((element) => element.remove());

  document.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      if (/^on/i.test(attribute.name)) {
        element.removeAttribute(attribute.name);
      }

      if (
        ["href", "src"].includes(attribute.name.toLowerCase()) &&
        /^\s*javascript:/i.test(attribute.value)
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return document.body.innerHTML;
};

const isInlineHtml = (content) =>
  typeof content === "string" && /^\s*</.test(content);

const resolveQuizContentPath = (content) => {
  if (
    typeof content !== "string" ||
    isInlineHtml(content) ||
    /^(https?:)?\/\//i.test(content)
  ) {
    return content;
  }

  const publicUrl = process.env.PUBLIC_URL || "";

  if (!publicUrl || !content.startsWith("/")) {
    return content;
  }

  return `${publicUrl.replace(/\/$/, "")}${content}`;
};

const QuizViewer = ({ quiz, onBack }) => {
  const [quizHtml, setQuizHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadQuiz = async () => {
      setLoading(true);
      setError("");

      try {
        let html = quiz.content;

        if (!isInlineHtml(quiz.content)) {
          const response = await fetch(resolveQuizContentPath(quiz.content));
          if (!response.ok) {
            throw new Error(`${response.status} ${response.statusText}`);
          }
          html = await response.text();
        }

        if (isMounted) {
          setQuizHtml(sanitizeQuizHtml(html));
        }
      } catch {
        if (isMounted) {
          setError("Could not load this quiz. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadQuiz();

    return () => {
      isMounted = false;
    };
  }, [quiz]);

  return (
    <div className="post-container quiz-viewer">
      <button
        type="button"
        className="secondary-btn quiz-back-btn"
        onClick={onBack}
      >
        Back to quizzes
      </button>
      <div className="post-header quiz-post-header">
        <span className="category-tag">{quiz.category}</span>
        <h1>{quiz.title}</h1>
        <div className="post-meta">
          <span>{quiz.questionCount} questions</span>
          <span className="post-reading-time">{quiz.estimatedTime}</span>
          {quiz.difficulty && <span>{quiz.difficulty}</span>}
        </div>
      </div>

      {loading && (
        <div className="blog-loading blog-loading-inline">
          <div className="loading-spinner"></div>
          <p>Loading quiz...</p>
        </div>
      )}
      {error && <p className="blog-error-message">{error}</p>}
      {!loading && !error && (
        <article
          className="blog-post quiz-html-content"
          dangerouslySetInnerHTML={{ __html: quizHtml }}
        />
      )}
    </div>
  );
};

export default QuizViewer;
